/**
 * @file AdminInvoicesFilters.tsx
 * @brief Filter bar for the platform-wide (super-admin) invoices table.
 *
 * @details
 * Flush `GridCells` layout matched to Sessions/Users filters (29-07-2026).
 * Mirrors company invoices filters but uses `CompanyPicker` for tenant scope.
 *
 * @author DES
 * @date 08-07-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: period / company became header pills
 *          (`AdminInvoicesHeaderFilters`), the custom range the page header's
 *          date circle; no Clear button. This file's bar is the search.
 */

import { useEffect, useState } from "react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { GridCell } from "@/features/analytics/components/GridBlock";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { AdminInvoicesListParams, InvoicePeriod } from "../schemas";

/** "All time" leads: it is the unset value. */
const PERIOD_ITEMS: SearchableMenuItem[] = [
  { value: "all", label: "All time" },
  { value: "mtd", label: "This month" },
  { value: "3m", label: "Last 3 months" },
];

interface Props {
  value: AdminInvoicesListParams;
  onChange: (next: AdminInvoicesListParams) => void;
}

/** Period and company as the page header's text pills. */
export function AdminInvoicesHeaderFilters({ value, onChange }: Props) {
  const period = value.period ?? "all";
  return (
    <>
      <HeaderFilterMenu
        title="Filter by period"
        searchPlaceholder="Search periods"
        items={PERIOD_ITEMS}
        selected={period}
        label={
          PERIOD_ITEMS.find((i) => i.value === period)?.label ?? PERIOD_ITEMS[0].label
        }
        active={period !== "all"}
        onClear={() =>
          onChange({ ...value, period: "all", from: undefined, to: undefined, page: 1 })
        }
        onSelect={(v) =>
          onChange({
            ...value,
            period: v as InvoicePeriod,
            from: undefined,
            to: undefined,
            page: 1,
          })
        }
      />
      <CompanyPicker
        variant="pill"
        value={value.company_id ?? null}
        onChange={(id) =>
          onChange({ ...value, company_id: id ?? undefined, page: 1 })
        }
        placeholder="All companies"
      />
    </>
  );
}

/** The search, rendered by the page inside its bottom dock (DES 01-10-2026). */
export function AdminInvoicesFilters({ value, onChange }: Props) {
  const [searchInput, setSearchInput] = useState(value.q ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.q ?? "") === debouncedSearch) return;
    onChange({ ...value, q: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    // The Notes screen's search (DES 01-10-2026), rendered by the page
    // inside its bottom PageDock ("dock style ki tarha neechay").
    <ExpandingSearch
      value={searchInput}
      onChange={(next) => setSearchInput(next)}
      placeholder="Invoice ID, employee, email, or company"
      label="Search invoices"
    />
  );
}

/** Under the header, while a custom range is set (it stays in the row; the
 *  search moved to the page dock, DES 01-10-2026). */
export function AdminInvoicesDateNote({ value }: Pick<Props, "value">) {
  if (!value.from && !value.to) return null;
  return (
    <GridCell padded={false}>
      <p className="px-4 py-2 text-xs text-muted-foreground md:px-5">
        Custom date range overrides the period preset.
      </p>
    </GridCell>
  );
}
